import type uk from "./uk";

/** EN catalogue — must mirror the shape of `uk` (key-parity type safety). */
const en: typeof uk = {
  app: {
    title: "Salonos",
  },
  layout: {
    aria: {
      sidebar: "Sidebar navigation",
      sections: "Sections",
      moreSections: "Settings sections",
      topbar: "Top bar",
      bottomnav: "Bottom navigation",
    },
    account: {
      menuLabel: "Account menu",
      logout: "Log out",
    },
    more: {
      title: "More",
      description: "Salon settings sections",
      close: "Close",
    },
  },
  nav: {
    zones: {
      daily: "Daily work",
      settings: "Salon settings",
    },
    journal: "Bookings",
    schedule: "Schedule",
    more: "More",
    clients: "Clients",
    summary: "Summary",
    masters: "Masters",
    services: "Services",
    salonProfile: "Salon profile",
  },
  sections: {
    inDevelopment: "Section under development",
    inDevelopmentHint: "This section is coming soon.",
  },
  services: {
    title: "Services",
    subtitle: "Manage your salon's service catalog",
    loading: "Loading…",
    listError: "Couldn't load services",
    retry: "Try again",
    saving: "saving…",
    assignedCount: "{count} master | {count} masters",
    row: {
      addMaster: "Master",
    },
    draft: {
      placeholder: "Add a service",
      placeholderNext: "Next service…",
      addToCategory: "Add a service to {name}",
    },
    actions: {
      rename: "Rename",
      delete: "Delete",
      save: "Save",
      cancel: "Cancel",
      retry: "Retry",
      dismiss: "Dismiss",
      confirm: "Confirm",
      addService: "Add service",
      move: "Move to category",
      moveToCategory: "Move to category",
      moveHere: "Move here",
    },
    detail: {
      sheetTitle: "Service masters",
      ctxLabel: "Service",
      mastersSection: "Masters",
      masterCount: "{count} master | {count} masters",
      addMaster: "Add master",
      manageMenuLabel: "Manage service",
      renameConfirm: "Confirm",
      renameCancel: "Cancel",
      currentCategoryHint: "current",
      empty: {
        title: "No masters assigned to this service yet",
        body: "Add a master and set the price and duration for this service. Without a master, the service won't appear in bookings.",
      },
      master: {
        meta: "{duration} min · {price} · buffer {buffer} min",
        bufferMeta: "buffer {buffer} min",
        edit: "Edit",
      },
      assignedError: "Couldn't load masters",
      retry: "Retry",
      addMoreMaster: "+ Add another master",
      done: "Done",
      bannerAdded: "{name} added to \"{service}\"",
      closeLabel: "Close",
    },
    categories: {
      title: "Uncategorized",
      add: "New category",
      saved: "Category saved",
      deleted: "Category deleted",
      addPlaceholder: "Category name",
      uncategorized: "Uncategorized",
      assignHint: "To assign a category, open a service → ⋯ → Move to category.",
      renameCategory: "Rename category",
      deleteCategory: "Delete category",
      deleteKeepsServices: "Services move to Uncategorized, they are not deleted.",
      count: "{count} service | {count} services",
    },
    delete: {
      confirm: "Delete “{name}”?",
      base: "The service will be removed from the catalog. This action cannot be undone.",
      assignedWarning: "{count} master is assigned to this service. They will be detached along with it. | {count} masters are assigned to this service. They will be detached along with it.",
      blocked: "The service is assigned to masters. Detach it in the master cards first.",
    },
    errors: {
      invalidName: "Enter a valid name (up to 200 characters).",
      notFound: "The service has already been deleted.",
      inUse: "The service is assigned to masters. Detach it in the master cards first.",
      serviceInUse: "Master has scheduled bookings for this service. Cancel or reschedule first.",
    },
    empty: {
      category: "No services in this category yet.",
      firstRun: {
        title: "Service catalog is empty",
        body: "Group services into categories — it helps clients navigate.",
        cta: "New category",
      },
    },
    a11y: {
      editLabel: "Service name",
      savedAnnounce: "{name} saved",
      deletedAnnounce: "{name} deleted",
      serviceActions: "Actions for service \"{name}\"",
      categoryActions: "Actions for category \"{name}\"",
      moveLabel: "Category for \"{name}\"",
      collapseCategory: "Collapse category \"{name}\"",
      expandCategory: "Expand category \"{name}\"",
    },
    assign: {
      picker: {
        title: "Choose a master",
        search: "Search master…",
        next: "Next — configure",
        createNew: "Create a new master",
      },
      config: {
        save: "Save",
        cancel: "Cancel",
        detach: "Detach",
      },
      create: {
        nameLabel: "Name",
        phoneLabel: "Phone",
        hint: "The master is linked to the service — edit details on the masters page",
        mastersLink: "Masters page",
      },
    },
    detach: {
      trigger: "Detach master",
      dangerLabel: "Danger zone",
      hint: "The master will no longer offer this service. This link's price and duration will be lost.",
      confirmTitle: "Detach master from service?",
      confirmBody: "{master} will no longer offer \"{service}\". The entered price and duration will be deleted — you'll have to set them again when re-assigning.",
      confirm: "Detach",
      close: "Close",
    },
  },
  masters: {
    title: "Masters",
    subtitle: "Add your salon's masters, edit and delete their profiles.",
    add: "Add a master",
    backToList: "Back to list",
    setupHint: {
      services: "Select a master to add service prices and durations",
      schedule: "Select a master to configure their work schedule",
    },
    tabs: {
      profile: "Profile",
      photo: "Photo",
      schedule: "Schedule",
      services: "Services",
    },
    photo: {
      empty: "No photo yet",
      hint: "JPEG or PNG",
      actions: {
        upload: "Upload photo",
        replace: "Replace photo",
        remove: "Remove photo",
        confirm: "Upload",
        cancel: "Cancel",
      },
      remove: {
        title: "Remove photo?",
        description: "The master's photo will be deleted. You can undo this by uploading a photo again.",
        confirm: "Remove",
      },
      a11y: {
        preview: "Photo of {name}",
        uploaded: "Photo updated",
        removed: "Photo removed",
        changeButton: "Change master photo",
      },
      errors: {
        unsupportedType: "Only JPEG and PNG are supported",
        invalidImage: "Couldn't read the image or its dimensions are too large",
        tooLarge: "File is too large",
        uploadFailed: "Couldn't upload the photo",
        masterGone: "This master is gone, refresh the list",
      },
    },
    loading: "Loading…",
    listError: "Couldn't load masters",
    retry: "Try again",
    empty: {
      title: "No masters yet",
      body: "Add your first master — they'll appear in the list and become available for bookings.",
      cta: "Add a master",
    },
    loadError: "Couldn't load the master",
    fields: {
      firstName: "First name",
      lastName: "Last name",
      middleName: "Middle name",
      email: "Email",
      phone: "Phone",
      hint: {
        middleName: "optional",
      },
    },
    actions: {
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      create: "Add a master",
    },
    delete: {
      title: "Delete this master?",
      confirm: "This action is irreversible. The profile of {name} will be permanently deleted.",
      blocked: "This master has assigned services. Detach them first, then the profile can be deleted.",
    },
    notFound: {
      title: "This master is gone",
      body: "This master was deleted or the link is invalid.",
      backCta: "Back to list",
    },
    a11y: {
      createdAnnounce: "Master {name} added",
      savedAnnounce: "Profile changes for {name} saved",
      deletedAnnounce: "Master deleted",
      avatarLabel: "Master avatar",
    },
    sections: {
      profile: "Profile",
      services: "Services",
      schedule: "Schedule",
      portfolio: "Portfolio",
      dangerZone: "Danger zone",
    },
    dangerZone: {
      description: "Deleting a master is irreversible.",
    },
    errors: {
      firstNameRequired: "Enter a first name",
      lastNameRequired: "Enter a last name",
      nameTooLong: "Value is too long (maximum 100 characters)",
      invalidName: "Check the master's name",
      invalidFirstName: "Check the first name",
      invalidMiddleName: "Check the middle name",
      invalidLastName: "Check the last name",
      invalidEmail: "Invalid email format",
      invalidPhone: "Invalid phone format",
      inUse: "This master has assigned services. Detach them first.",
      notFound: "This master is gone",
    },
    schedule: {
      weekdays: {
        "1": "Monday",
        "2": "Tuesday",
        "3": "Wednesday",
        "4": "Thursday",
        "5": "Friday",
        "6": "Saturday",
        "7": "Sunday",
      },
      dayOff: "Day off",
      from: "Start",
      to: "End",
      save: "Save schedule",
      edit: "Edit schedule",
      loadError: "Couldn't load schedule",
      empty: {
        title: "Schedule not set up",
        description: "Without a schedule this master is unavailable for online booking.",
      },
      a11y: {
        saved: "Schedule saved",
        dayToggle: "{day} — working day",
        startLabel: "Start — {day}",
        endLabel: "End — {day}",
        loading: "Loading schedule…",
      },
      setup: {
        cta: "Set up schedule",
        title: "Set up schedule",
        presets: {
          monFri: "Mon–Fri",
          monSat: "Mon–Sat",
          daily: "Every day",
          clearAll: "Clear",
        },
        apply: "Apply",
        close: "Close",
      },
      errors: {
        startBeforeEnd: "Start time must be before end time",
        invalid_working_hours: "Check working hours (start before end, 15-min step)",
        invalid_weekday: "Invalid weekday",
        duplicate_weekday: "Weekday specified twice",
        saveFailed: "Failed to save schedule",
      },
    },
    services: {
      saved: "Service saved",
      removed: "Service detached",
      unknownService: "[Deleted]",
      unknownMaster: "Unknown master",
      errors: {
        serviceRequired: "Select a service",
        invalidDuration: "Enter a valid duration (minutes, 1–480)",
        invalidPrice: "Enter a valid price (from 0)",
        invalidBuffer: "Enter a valid buffer (minutes, 0–120)",
        serviceGone: "The service was removed from the catalogue. List refreshed.",
        linkGone: "The assignment was already detached in another session",
        serviceInUse: "Master has scheduled bookings for this service. Cancel or reschedule first.",
      },
      add: "Add service",
      loading: "Loading…",
      listError: "Couldn't load services",
      retry: "Try again",
      empty: {
        title: "No services yet",
        body: "Add the services this master provides.",
        cta: "Add service",
      },
      emptyCatalog: {
        title: "Create services first",
        body: "Your salon has no services yet. Create them in the Services section.",
        cta: "Go to services",
      },
      row: {
        summary: "{duration} min · {price} · buffer {buffer} min",
      },
      units: {
        min: "min",
        hour: "h",
      },
      actions: {
        edit: "Edit",
        detach: "Remove",
        save: "Save",
        cancel: "Cancel",
      },
      form: {
        addTitle: "Add service",
        editTitle: "Edit service",
        service: "Service",
        servicePlaceholder: "Choose a service",
        duration: "Duration",
        price: "Price, €",
        buffer: "Buffer, min",
        bufferHint: "Time after the booking",
      },
      detach: {
        title: "Remove service?",
        description: "The service will be detached from this master. Link attributes will be lost.",
        confirm: "Remove",
      },
      a11y: {
        addedAnnounce: "{name} added",
        savedAnnounce: "{name} updated",
        detachedAnnounce: "{name} removed",
      },
    },
    portfolio: {
      title: "Portfolio",
      description: "Master's work photos",
      empty: "No work photos yet",
      loading: "Loading...",
      error: "Failed to load",
      retry: "Retry",
      addPhoto: "Add photo",
      full: "Portfolio is full (20 photos max)",
      cover: "Cover",
      upload: {
        title: "Add photo",
        fileLabel: "Work photo",
        serviceLabel: "Service (optional)",
        noTag: "No tag (general work)",
        submit: "Upload",
        cancel: "Cancel",
        preview: "Preview",
      },
      remove: {
        title: "Delete photo",
        description: "This photo will be removed from the portfolio. This cannot be undone.",
        confirm: "Delete",
        cancel: "Cancel",
      },
      errors: {
        full: "Portfolio already contains 20 photos",
        serviceNotOffered: "This master doesn't offer this service",
        unsupportedType: "Unsupported image type (JPEG or PNG only)",
        invalidImage: "The image is corrupted or invalid",
        tooLarge: "File is too large",
        reorderFailed: "Failed to reorder — please try again",
        uploadFailed: "Upload failed",
        masterGone: "Master not found",
      },
      a11y: {
        photoAlt: "Work photo",
        taggedPhotoAlt: "Work photo — {service}",
        photoAdded: "Photo added to portfolio",
        photoDeleted: "Photo removed from portfolio",
        photoMoved: "Photo moved to position {position} of {total}",
        addPhoto: "Add photo",
        moveBack: "Move back",
        moveForward: "Move forward",
        deletePhoto: "Delete photo",
      },
    },
  },
  journal: {
    loadError: "Failed to load schedule",
    retry: "Try again",
    toolbar: {
      today: "Today",
      prevDay: "Previous day",
      nextDay: "Next day",
      dateLabel: "Date navigation",
      viewSwitcher: "Switch view",
      viewGrid: "Grid",
      viewList: "List",
    },
    masterTabs: {
      all: "All",
      dayOff: "off",
      dayOffAria: "day off",
    },
    workingCount: "{n} of {m} masters",
    summary: {
      completed: "{count} completed",
      revenue: "revenue {amount}",
    },
    empty: {
      noMasters: "Nobody is working today",
      noBookings: "No bookings",
      selectMaster: "Select a master from the tabs",
    },
    actions: {
      addBooking: "New booking",
      reschedule: "Reschedule",
      cancel: "Cancel",
    },
    create: {
      title: "New booking",
      close: "Close",
      master: "Master",
      selectMaster: "Select master",
      service: "Service",
      selectService: "Select service",
      clientName: "Client name",
      clientPhone: "Phone",
      time: "Time",
      submit: "Add booking",
      servicesLoadError: "Failed to load services. Try selecting the master again.",
      noServicesForMaster: "No services configured for this master",
    },
    reschedule: {
      title: "Reschedule booking",
      close: "Close",
      master: "Master (change)",
      selectMaster: "Select master",
      date: "Date",
      dateGroupLabel: "Reschedule date",
      currentDay: "current",
      todayDay: "today",
      time: "New time",
      submit: "Save changes",
    },
    cancel: {
      title: "Cancel booking",
      description: "The booking will be cancelled. This cannot be undone.",
      reason: "Reason (optional)",
      confirm: "Cancel booking",
      close: "Back",
    },
    dateTimePicker: {
      idleHint: "Select master and service to see available times",
      timeLabel: "Select time",
      hoursLabel: "Hours",
      minutesLabel: "Minutes",
    },
    slotPicker: {
      empty: "No available slots",
      loading: "Loading slots…",
      error: "Failed to load available slots",
      emptyForDate: "No available slots for the selected date",
      emptyToday: "No more time available today",
    },
    errors: {
      slotTaken: "The slot was just taken. Please choose another time.",
      concurrentModification: "The booking has changed. Refresh and try again.",
      outsideWorkingHours: "The time is outside the master's working hours.",
      notEditable: "This booking can no longer be edited.",
      serviceNotConfigured: "The master no longer provides this service.",
      invalidPhone: "Invalid phone format.",
      invalidClientName: "Enter a valid client name.",
      notFound: "The booking no longer exists.",
      reloadAction: "Refresh",
    },
    a11y: {
      blockLabel: "{service}, {client}, {start}–{end}, {master}, {status}",
      createdAnnounce: "Booking created",
      rescheduledAnnounce: "Booking rescheduled",
      cancelledAnnounce: "Booking cancelled",
      bookingActions: "Booking actions",
      bookFreeSlot: "Book at {time}",
    },
    status: {
      scheduled: "Scheduled",
      completed: "Completed",
      cancelled: "Cancelled",
    },
    agenda: {
      dayOff: {
        title: "Day off",
        subtitle: "{name} is not working today. Bookings unavailable.",
      },
      empty: {
        title: "No bookings for this day",
        workingHours: "{name} works {start}–{end}",
      },
      duration: {
        hm: "{h}h {m}m",
        h: "{h}h",
        m: "{m}m",
      },
      freeSlot: {
        start: "Shift starts at {time} · {duration}",
        end: "Shift done · free {duration}",
        midLabel: "Free",
        book: "Book",
      },
    },
    timeBlock: {
      busy: "Occupied",
      createTitle: "Block time",
      deleteTitle: "Delete block",
      deleteDescription: "This time block will be removed for this master.",
      masterLabel: "Master",
      selectMaster: "Select master",
      rangeLabel: "Time range",
      reasonLabel: "Reason",
      reasonPlaceholder: "E.g.: lunch, break",
      submit: "Block",
      close: "Close",
      confirmDelete: "Delete",
      cancelDelete: "Cancel",
      createdAnnounce: "Time block created",
      errors: {
        overlapBlock: "This time overlaps with another block for this master.",
        overlapBooking: "This time overlaps with an existing booking.",
        invalidRange: "The end time must be after the start time.",
        reasonTooLong: "Reason is too long (max 200 characters).",
        notFound: "The block has already been deleted.",
      },
    },
    rebuildPlaceholder: "Coming soon",
  },
  auth: {
    login: {
      title: "Sign in to your cabinet",
      submit: "Sign in",
      forgotPassword: "Forgot password?",
      noAccount: "Don’t have an account?",
      signUp: "Sign up",
      supportPrompt: "Having trouble?",
      supportLink: "Contact support",
    },
    brand: {
      title: "Run your company in one place",
      subtitle: "Bookings, masters and analytics — in a single panel.",
      props: [
        "24/7 online booking",
        "Client journal and reminders",
        "Analytics and daily summaries",
      ],
    },
    fields: {
      email: "Email",
      emailPlaceholder: "you{'@'}example.com",
      password: "Password",
      passwordPlaceholder: "Your password",
      remember: "Remember me",
    },
    errors: {
      invalidCredentials: "Invalid email or password",
      validation: {
        emailRequired: "Enter your email",
        emailInvalid: "Invalid email format",
        passwordRequired: "Enter your password",
      },
    },
  },
  onboarding: {
    title: 'Set up your salon',
    subtitle: 'Complete the steps to open your salon to clients',
    progress: '{n} of {total}',
    steps: {
      services: {
        label: 'Services',
        desc: 'Add the list of services your salon provides',
      },
      masters: {
        label: 'Masters',
        desc: 'Add the masters working at your salon',
      },
      pricing: {
        label: 'Service prices & duration',
        desc: 'Set the duration and price for each service',
        lockHint: 'Add services and masters first',
      },
      schedule: {
        label: 'Masters schedule',
        desc: 'Set the working hours for each master',
        lockHint: 'Add a master first',
      },
      profile: {
        label: 'Salon profile',
        desc: 'Fill in salon information for the website',
        tag: 'for website',
      },
    },
    cta: {
      start: 'Start',
      open: 'Open',
      showHint: 'Show hint',
    },
    fullSetup: {
      title: 'Full setup complete',
      description: 'Your salon is configured and visible to clients',
    },
    stateError: 'Failed to check status, try refreshing',
    retry: 'Refresh',
  },
  errors: {
    validation: "Please check the entered data",
    sessionExpired: "Your session has ended. Please sign in again",
    forbidden: "Action declined. Refresh the page and try again",
    notFound: "Not found",
    conflict: "Action cannot be completed: the resource is in use",
    rateLimited: "Too many attempts. Please try again later",
    server: "Something went wrong. Please try again later",
    network: "The server is temporarily unavailable. Please try again",
    unknown: "An error occurred. Please try again later",
    boot: {
      title: "Couldn’t load your cabinet",
      description: "Check your connection and try again",
      retry: "Try again",
      loading: "Loading your cabinet…",
    },
  },
  publicPages: {
    signup: {
      title: "Sign up",
      subtitle: "Self-service sign-up is coming soon. Write to us and we’ll set up your cabinet.",
      backToLogin: "Back to sign in",
    },
    forgotPassword: {
      title: "Password recovery",
      subtitle: "Password reset is coming soon. Contact support and we’ll help you sign in.",
      backToLogin: "Back to sign in",
    },
  },
  clients: {
    title: "Clients",
    subtitle: "Client cards for your salon: add, find, edit and delete.",
    add: "Add client",
    backToList: "Back to list",
    loading: "Loading…",
    listError: "Couldn't load clients",
    loadError: "Couldn't load client",
    retry: "Try again",
    search: {
      label: "Find a client",
      placeholder: "Name or phone",
      clear: "Clear search",
      searching: "Searching…",
      minLengthHint: "Enter at least 2 characters to search.",
      error: "Search failed",
    },
    results: {
      title: "Results",
      limitHint: "Showing the first {count} matches. Refine your query to narrow it down.",
      empty: "Nothing found",
      emptyBody: "Check the spelling or create a new client card.",
      emptyCta: "Create \"{query}\"",
    },
    recent: {
      title: "Recent",
      hint: "Recently added clients. To find anyone in the full database — use the search above.",
    },
    empty: {
      title: "No clients yet",
      body: "Add your first client — their card will appear in the list.",
      cta: "Add client",
    },
    fields: {
      firstName: "First name",
      lastName: "Last name",
      phone: "Phone",
      email: "Email",
      hint: { optional: "optional" },
    },
    create: {
      title: "New client",
      close: "Close",
      submit: "Add client",
    },
    actions: {
      create: "Add client",
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
    },
    delete: {
      title: "Delete this client?",
      confirm: "This action is irreversible. The client card for {name} will be permanently deleted.",
    },
    notFound: {
      title: "This client is gone",
      body: "This client was deleted or the link is invalid.",
      backCta: "Back to list",
    },
    dangerZone: {
      description: "Deleting a client is irreversible.",
    },
    a11y: {
      createdAnnounce: "Client {name} added",
      savedAnnounce: "Changes to {name}'s card saved",
      deletedAnnounce: "Client deleted",
      searchResults: "Found {count}",
      avatarLabel: "Client avatar",
    },
    errors: {
      firstNameRequired: "Enter a first name",
      phoneRequired: "Enter a phone number",
      nameTooLong: "Value is too long (maximum 100 characters)",
      invalidName: "Check the first name and last name",
      invalidEmail: "Invalid email format",
      invalidPhone: "Invalid phone format",
      phoneExists: "A client with this phone number already exists",
      notFound: "This client is gone",
    },
  },
  salon: {
    title: "Salon profile",
    loading: "Loading…",
    loadError: "Failed to load salon profile",
    retry: "Try again",
    profile: {
      title: "Basic information",
      fields: {
        name: "Salon name",
        address: "Address",
        phone: "Phone",
        description: "Description",
      },
      hint: {
        name: "required",
        description: "up to 1000 characters",
      },
      save: "Save",
      saved: "Changes saved",
      errors: {
        nameRequired: "Enter the salon name",
        nameInvalid: "Check the name (up to 255 characters)",
        addressTooLong: "Address is too long (up to 255 characters)",
        phoneTooLong: "Phone is too long (up to 32 characters)",
        descriptionTooLong: "Description is too long (up to 1000 characters)",
        saveFailed: "Failed to save",
        notFound: "Salon profile not found",
      },
    },
    gallery: {
      title: "Interior photos",
      loadError: "Failed to load photos",
      empty: "Add your first interior photo",
      addPhoto: "Add photo",
      uploaded: "Photo uploaded",
      deleted: "Photo deleted",
      full: "Maximum 10 photos",
      errors: {
        full: "Maximum of 10 photos reached",
        unsupportedType: "Only JPEG and PNG are supported",
        invalidImage: "Could not read the image",
        tooLarge: "File is too large",
        uploadFailed: "Failed to upload photo",
        reorderFailed: "Failed to reorder photos",
      },
      a11y: {
        photoAlt: "Interior photo {n}",
        moveLeft: "Move photo {n} to the left",
        moveRight: "Move photo {n} to the right",
        delete: "Delete photo {n}",
        moved: "Photo moved to position {pos} of {total}",
        cover: "Cover photo",
      },
      deleteConfirm: {
        title: "Delete photo?",
        description: "The photo will be permanently deleted.",
        cancel: "Cancel",
        confirm: "Delete",
      },
    },
  },
  records: {
    dayEmpty: "No master works this day",
    offWork: {
      label: "off-hours",
    },
    actions: {
      pastTooltip: "Past booking",
    },
    aria: {
      gridLabel: "Schedule",
      createCell: "Select action for cell",
      dateStrip: "Date selection",
    },
    createStub: {
      title: "Coming soon",
      message: "Booking creation will be available soon",
    },
    chooser: {
      title: "Choose an action",
      createBooking: "Create booking",
      blockTime: "Block time",
      cancel: "Cancel",
      close: "Close",
    },
    createFlow: {
      sections: {
        client: "Client",
        service: "Service",
        time: "Time",
      },
      clientStep: {
        searchPlaceholder: "Name or phone…",
        minLengthHint: "Enter at least 2 characters",
        searching: "Searching…",
        limited: "Showing first results",
        emptyTitle: "Nothing found",
        addNew: "Add new client",
      },
      inlineCreate: {
        title: "New client",
        firstName: "First name",
        phone: "Phone",
        lastName: "Last name",
        optional: "(optional)",
        submit: "Create and continue",
        back: "Back to search",
        phoneExists: "A client with this phone already exists",
        createError: "Failed to create client",
      },
      summary: {
        change: "Change",
      },
      time: {
        nearestHint: "{tapped} not available — chose {chosen}",
        emptyDay: "No available time for this day",
      },
      noServiceGuard: "Select a service to save the booking",
    },
  },
  schedule: {
    title: "Schedule",
    loading: "Loading…",
    loadError: "Failed to load schedule",
    retry: "Try again",
    empty: {
      title: "No schedule yet",
      body: "Add masters and configure their schedule.",
    },
    nav: {
      today: "Today",
      todayAria: "Return to today",
      todayAnnounce: "Returned to current month",
      monthPickerAria: "Select month",
      monthSelectedAnnounce: "Selected {month} {year}",
    },
    cell: {
      working: "Working day",
      dayOff: "off",
      notSet: "not set",
      aria: {
        working: "{master}, {date} — working hours {start}–{end}",
        dayOff: "{master}, {date} — day off",
        notSet: "{master}, {date} — schedule not set",
      },
    },
    sheet: {
      title: "Actions",
      createBooking: "Create booking",
      fieldLabel: "Working hours for this date",
      save: "Save",
      invalidWindow: "End must be later than start",
      close: "Close",
    },
    a11y: {
      gridLabel: "Schedule grid",
      masterColumn: "Master",
      loading: "Loading schedule…",
    },
    header: {
      setup: "Set up schedule",
    },
    mode: {
      banner: "Settings mode",
      subtitle: "select cells to edit",
      exit: "Cancel",
      enteredAnnounce: "Schedule settings mode enabled",
    },
    selection: {
      selectColumnAria: "Select entire column — {date}",
      selectRowAria: "Select entire row — {master}",
      cellSelectedAria: "{master}, {date} — selected",
    },
    editor: {
      dayOff: "Day off",
      start: "Start",
      end: "End",
      counter: "Update {count} cell | Update {count} cells",
      apply: "Apply",
    },
    bulk: {
      confirmTitle: "Apply schedule?",
      confirmWindow: "Set {start}–{end} for {count} cells.",
      confirmDayOff: "Mark {count} cells as day off.",
      confirmOverwrite: "{count} of them already have hours — they will be replaced.",
      applyingLabel: "Applying…",
      appliedToast: "Schedule updated",
      partialHint: "Days with no hours set are shown as day off. To set hours, edit those days separately.",
      conflictBlocked: "Some dates have bookings that don't fit the new window. Resolve them in the journal first.",
      masterGone: "Master no longer exists. Schedule refreshed.",
      invalidWindow: "Invalid working window.",
      genericError: "Failed to apply schedule. Please try again.",
    },
    filter: {
      title: "Show masters",
      subtitle: "Hidden masters disappear from the schedule. Records and data are unchanged.",
      visibleOf: "Showing {n} of {m}",
      showAll: "Show all",
      done: "Done",
      triggerLabel: "Masters",
      triggerAria: "Master filter, {count} hidden",
      allHiddenTitle: "All masters are hidden",
      allHiddenBody: "You've hidden all masters. Bring them back to the schedule to see the timetable.",
    },
  },
  monthPicker: {
    title: "Select month",
    prevYear: "Previous year",
    nextYear: "Next year",
    currentSuffix: ", current month",
    disabledSuffix: ", unavailable",
    close: "Close",
  },
};

export default en;
